import {
  dayBounds,
  formatMin,
  LIMITS,
  MINUTES_PER_DAY,
  nextGridLine,
  parseWallTimeText,
  type LiveProject,
  type ProjectsById,
} from '@wiztime/core';
import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../lib/cx';
import { projectHex } from '../../../lib/projectColor';
import { categoryTagOf } from '../../../lib/projectLabel';
import { toastInfo } from '../../../shell/toastStore';
import { Button, ButtonVariant } from '../../../ui/Button';
import { ChevronDownIcon } from '../../../ui/icons';
import { TimeField } from '../../../ui/TimeField';
import { PickerFocus, useDayUi } from '../dayUiStore';
import { childCountOf, flatGroups, groupRows, toggled, type ProjectGroup } from '../projectGroups';
import { chooseByDigit, ManageProjectsLink, ProjectDropdown, SHORTCUT_COUNT } from './ProjectDropdown';
import {
  baselineTextOf,
  initPickerFields,
  parsePickerEnd,
  parsePickerStart,
  pickerDiff,
  PickerTextField,
  PickerTimeError,
  type PickerBaseline,
  type PickerFields,
} from './pickerModel';
import styles from './ProjectPicker.module.css';

export interface ProjectPickerProps {
  /** The field baselines of the open target; also installed in the store by the host. */
  readonly baseline: PickerBaseline;
  /** Create context: no record exists yet; choosing a project creates the block. */
  readonly creating: boolean;
  /** Number of merged blocks the commit applies to (1 for a single block). */
  readonly mergedCount: number;
  /** Merged group whose members carry different notes (2026-10b §5.5). */
  readonly notesDiffer: boolean;
  /** Selectable projects in display order (recent use first), children included. */
  readonly projects: readonly LiveProject[];
  /**
   * `projects` as top-level projects with their children, fold rule applied (project hierarchy).
   * Omitted: every project is shown flat.
   */
  readonly groups?: readonly ProjectGroup[];
  /** Every project, to resolve a child project's inherited colour. */
  readonly projectsById?: ProjectsById;
  /** Selectable top-level projects in settings order: the digit keys 1-9 pick from this list (§4.8, §5.3); children have no digit. */
  readonly digitProjects: readonly LiveProject[];
  readonly currentProjectId: string | null;
  /** The block's current project (possibly deactivated, so not necessarily in `projects`); null in the create context. */
  readonly currentProject?: LiveProject | null;
  /** Where the initial focus goes (F2 focuses the title field, §5.3). */
  readonly focus?: PickerFocus;
  /** Rendered as a bottom sheet (phone): single project column stays two-up via media query. */
  readonly narrow: boolean;
  /** Confirmed blocks this one overlaps ("view overlaps (N)", §5.3). */
  readonly overlapCount: number;
  readonly onChoose: (projectId: string) => void;
  readonly onOverlay?: () => void;
  readonly onConfirm?: () => void;
  readonly onViewConflict?: () => void;
  readonly onDelete?: () => void;
  /** Save the modified fields and close (「保存」, Enter in the title, Ctrl/Cmd+Enter in a text field). */
  readonly onSaveClose: () => void;
  /** Discard the unsaved field edits and close (「取消」; Esc outside a modified input). */
  readonly onCancel: () => void;
}

/** Desktop: seven or more active projects switch the list to two columns (§5.3). */
const TWO_COLUMN_MIN_PROJECTS = 7;
/** The title counter appears from 32 characters, the note counter from 160 (§5.3). */
const TITLE_COUNTER_FROM = 32;
const NOTE_COUNTER_FROM = 160;

function sameTarget(a: PickerBaseline, b: PickerBaseline): boolean {
  return (
    a.date === b.date &&
    a.blockIds.length === b.blockIds.length &&
    a.blockIds.every((id, index) => b.blockIds[index] === id) &&
    (a.blockIds.length > 0 || (a.startMin === b.startMin && a.endMin === b.endMin))
  );
}

/**
 * The project picker (2026-10b §5.3): project list on top (two columns from seven active
 * projects on desktop), then the always-visible field area (time / title / note), then the
 * action rows. Field drafts live in `dayUiStore`; every close path commits them through
 * `flushPicker` (§5.4).
 */
export function ProjectPicker(props: ProjectPickerProps) {
  const { t } = useTranslation();
  const storeFields = useDayUi((state) => state.pickerFields);
  const setPickerText = useDayUi((state) => state.setPickerText);
  const resetPickerText = useDayUi((state) => state.resetPickerText);
  // Until the host's layout effect installs the baseline, render from a local equivalent.
  const fields: PickerFields =
    storeFields !== null && sameTarget(storeFields.baseline, props.baseline)
      ? storeFields
      : initPickerFields(props.baseline);
  const baseline = fields.baseline;
  const diff = pickerDiff(fields);

  const [startInvalid, setStartInvalid] = useState(false);
  const [endInvalid, setEndInvalid] = useState(false);
  // Create context, Enter in the title without a project yet: "please choose a project" (§5.3).
  const [chooseHint, setChooseHint] = useState(false);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [earlierOpen, setEarlierOpen] = useState<ReadonlySet<string>>(new Set());
  const groups = props.groups ?? flatGroups(props.projects);
  const rows = groupRows(groups, { expanded, earlierOpen });
  const byId = props.projectsById ?? new Map<string, LiveProject>();
  const listRef = useRef<HTMLUListElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const changeRef = useRef<HTMLDivElement>(null);

  // Only the create context shows the project list; an existing block changes its project
  // through the dropdown below the note (2026-10c §3).
  const twoCol = props.creating && !props.narrow && groups.length >= TWO_COLUMN_MIN_PROJECTS;
  const digitIndex = new Map(props.digitProjects.map((project, index) => [project.id, index]));
  const targetId = props.baseline.blockIds.join(',') || 'create';

  // After the panel is positioned (the popover and sheet do that in layout effects); re-run
  // when the picker is retargeted onto another block.
  useEffect(() => {
    if (props.focus === PickerFocus.Title) {
      titleRef.current?.focus({ preventScroll: true });
      return;
    }
    if (props.focus === PickerFocus.Start) {
      startRef.current?.querySelector('input')?.focus({ preventScroll: true });
      return;
    }
    const list = listRef.current;
    const target =
      list?.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ??
      list?.querySelector<HTMLButtonElement>('button') ??
      changeRef.current?.querySelector<HTMLButtonElement>('button');
    target?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId]);

  const onListKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (chooseByDigit(event, props.digitProjects, props.onChoose)) return;
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const buttons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    const index = buttons.findIndex((button) => button === event.target);
    if (index === -1) return;
    const step =
      event.key === 'ArrowUp'
        ? -(twoCol ? 2 : 1)
        : event.key === 'ArrowDown'
          ? twoCol
            ? 2
            : 1
          : event.key === 'ArrowLeft'
            ? -1
            : event.key === 'ArrowRight'
              ? 1
              : undefined;
    if (step === undefined) return;
    event.preventDefault();
    buttons[Math.min(buttons.length - 1, Math.max(0, index + step))]?.focus();
  };

  const textDirty = (field: PickerTextField): boolean => fields[field] !== baselineTextOf(fields, field);

  /** Esc inside a modified input restores its baseline and keeps the picker open (§5.4). */
  const onTextEscape =
    (field: PickerTextField) =>
    (event: KeyboardEvent<HTMLElement>): void => {
      if (event.key !== 'Escape' || !textDirty(field)) return;
      event.preventDefault();
      event.stopPropagation();
      resetPickerText(field);
    };

  const saveOrAskProject = (): void => {
    if (props.creating && props.currentProjectId === null) {
      setChooseHint(true);
      const list = listRef.current;
      (
        list?.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ??
        list?.querySelector<HTMLButtonElement>('button')
      )?.focus();
      return;
    }
    props.onSaveClose();
  };

  const onTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    onTextEscape(PickerTextField.Title)(event);
    if (event.defaultPrevented) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      saveOrAskProject();
    }
  };

  const onNoteKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    onTextEscape(PickerTextField.Note)(event);
    if (event.defaultPrevented) return;
    // Ctrl/Cmd+Enter inside a text field means "save and close" (§5.3); plain Enter wraps.
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      saveOrAskProject();
    }
  };

  const onPasteLimited =
    (limit: number, current: string) =>
    (event: ClipboardEvent<HTMLElement>): void => {
      const pasted = event.clipboardData.getData('text');
      if (current.length + pasted.length > limit) {
        toastInfo(t('picker.pasteTruncated', { limit }));
      }
    };

  const bounds = dayBounds(baseline.dayStartMin);
  const stepTime =
    (field: 'start' | 'end') =>
    (direction: 1 | -1, byHour: boolean): void => {
      const startAbs = parsePickerStart(fields.startText, baseline.dayStartMin) ?? baseline.startMin;
      if (field === 'start') {
        const current = startAbs;
        const moved = byHour ? current + direction * 60 : nextGridLine(current, baseline.stepMin, direction);
        const next = Math.min(Math.max(moved, bounds.startMin), bounds.endMin - 1);
        setPickerText(PickerTextField.Start, formatMin(next));
        setStartInvalid(false);
        return;
      }
      const current = parsePickerEnd(fields.endText, startAbs) ?? baseline.endMin;
      const moved = byHour ? current + direction * 60 : nextGridLine(current, baseline.stepMin, direction);
      const next = Math.min(Math.max(moved, startAbs + 1), bounds.endMin);
      setPickerText(PickerTextField.End, formatMin(next));
      setEndInvalid(false);
    };

  /** "2 小时 35 分钟" (§3.8): the live duration beside the time fields. */
  const durationLabel = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return t('picker.durationM', { m });
    return m === 0 ? t('picker.durationH', { h }) : t('picker.durationHM', { h, m });
  };

  const startAbs = parsePickerStart(fields.startText, baseline.dayStartMin);
  const endAbs = startAbs === null ? null : parsePickerEnd(fields.endText, startAbs);
  const timeInvalid = startInvalid || endInvalid;
  const outOfDay = diff.timeError === PickerTimeError.Range && diff.timeDirty;

  const titleCount = fields.title.length;
  const noteCount = fields.note.length;
  const noteRows = Math.min(4, Math.max(2, fields.note.split('\n').length));

  const deleteLabel =
    props.mergedCount > 1 ? t('picker.deleteMany', { count: props.mergedCount }) : t('picker.delete');

  const notice: ReactNode =
    props.creating && fields.noticeShown ? (
      <p className={styles.notice} role="alert" data-testid="picker-notice">
        <span aria-hidden="true" className={styles.noticeIcon}>
          !
        </span>
        {t('picker.createNotice')}
      </p>
    ) : chooseHint ? (
      <p className={styles.notice} role="alert" data-testid="picker-choose-hint">
        <span aria-hidden="true" className={styles.noticeIcon}>
          !
        </span>
        {t('picker.chooseProject')}
      </p>
    ) : null;

  return (
    <div
      className={cx(styles.picker, twoCol ? styles.twoCol : styles.single)}
      data-testid="project-picker"
      // Retrigger the shake on every repeated warning (§5.4); none under reduced motion (CSS).
      key={`shake-${fields.noticeCount}`}
      data-shake={props.creating && fields.noticeCount > 0 ? '' : undefined}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{t('picker.title')}</h2>
        <span className={styles.range} data-testid="picker-range">
          {`${formatMin(baseline.startMin)}–${formatMin(baseline.endMin)}`}
        </span>
      </header>
      {props.mergedCount > 1 ? (
        <p className={styles.hint}>{t('picker.merged', { count: props.mergedCount })}</p>
      ) : null}

      {props.creating ? (
        props.projects.length === 0 ? (
          <div className={styles.empty}>
            <p>{t('picker.empty')}</p>
            <ManageProjectsLink />
          </div>
        ) : (
          <div className={styles.listWrap}>
            <ul
              className={styles.list}
              aria-label={t('picker.projects')}
              ref={listRef}
              onKeyDown={onListKeyDown}
            >
              {rows.map((row, index) => {
                if (row.kind === 'earlier') {
                  const parentId = row.group.project.id;
                  return (
                    <li key={`earlier-${parentId}`} className={styles.childItem}>
                      <button
                        type="button"
                        className={styles.earlierToggle}
                        aria-expanded={row.open}
                        data-testid="picker-earlier"
                        onClick={() => setEarlierOpen((previous) => toggled(previous, parentId))}
                      >
                        {t('picker.earlier', { count: row.group.folded.length })}
                      </button>
                    </li>
                  );
                }
                const { project, level, group } = row;
                const digit = level === 0 ? (digitIndex.get(project.id) ?? SHORTCUT_COUNT) : SHORTCUT_COUNT;
                const category =
                  twoCol || level === 1
                    ? null
                    : categoryTagOf(project.data.name, t(`category.${project.data.category}`));
                const isCurrent = project.id === props.currentProjectId;
                const childCount = level === 0 ? childCountOf(group) : 0;
                const isOpen = expanded.has(project.id);
                return (
                  <li key={project.id} className={level === 1 ? styles.childItem : undefined}>
                    <div className={styles.projectRow}>
                      <button
                        type="button"
                        className={cx(styles.project, isCurrent && styles.current)}
                        aria-pressed={isCurrent}
                        aria-keyshortcuts={digit < SHORTCUT_COUNT ? String(digit + 1) : undefined}
                        title={project.data.name}
                        data-testid="picker-project"
                        data-level={level}
                        tabIndex={isCurrent || (props.currentProjectId === null && index === 0) ? 0 : -1}
                        onClick={() => props.onChoose(project.id)}
                      >
                        <span
                          className={styles.dot}
                          style={{ '--dot-color': projectHex(project, byId) } as CSSProperties}
                        />
                        <span className={styles.projectName}>{project.data.name}</span>
                        {category === null ? null : <span className={styles.category}>{category}</span>}
                        {!twoCol && digit < SHORTCUT_COUNT ? (
                          <span className={styles.shortcut} aria-hidden="true" data-testid="picker-shortcut">
                            {digit + 1}
                          </span>
                        ) : null}
                      </button>
                      {childCount > 0 ? (
                        <button
                          type="button"
                          className={cx(styles.expandToggle, isOpen && styles.expandToggleOpen)}
                          aria-expanded={isOpen}
                          aria-label={t(isOpen ? 'picker.collapseChildren' : 'picker.expandChildren', {
                            name: project.data.name,
                            count: childCount,
                          })}
                          title={t(isOpen ? 'picker.collapseChildren' : 'picker.expandChildren', {
                            name: project.data.name,
                            count: childCount,
                          })}
                          data-testid="picker-expand"
                          onClick={() => setExpanded((previous) => toggled(previous, project.id))}
                        >
                          <ChevronDownIcon />
                          <span className={styles.expandCount}>{childCount}</span>
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            <ManageProjectsLink />
          </div>
        )
      ) : null}

      {notice}

      <div className={styles.fields}>
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel} id="picker-time-label">
            {t('picker.fieldTime')}
          </span>
          <div className={styles.timeRow} ref={startRef}>
            <TimeField
              value={fields.startText}
              label={t('picker.startLabel')}
              invalid={startInvalid}
              nextDay={startAbs !== null && startAbs >= MINUTES_PER_DAY}
              disabled={!baseline.spanEditable}
              title={baseline.spanEditable ? undefined : t('picker.mergedTimeHint')}
              testId="picker-start"
              onChange={(text) => {
                setPickerText(PickerTextField.Start, text);
                setStartInvalid(false);
              }}
              onCommit={() => setStartInvalid(parseWallTimeText(fields.startText) === null)}
              onEscape={() => {
                if (!textDirty(PickerTextField.Start)) return false;
                resetPickerText(PickerTextField.Start);
                setStartInvalid(false);
                return true;
              }}
              onStep={stepTime('start')}
            />
            <span className={styles.timeDash} aria-hidden="true">
              –
            </span>
            <TimeField
              value={fields.endText}
              label={t('picker.endLabel')}
              invalid={endInvalid}
              nextDay={endAbs !== null && endAbs >= MINUTES_PER_DAY}
              disabled={!baseline.spanEditable}
              title={baseline.spanEditable ? undefined : t('picker.mergedTimeHint')}
              testId="picker-end"
              onChange={(text) => {
                setPickerText(PickerTextField.End, text);
                setEndInvalid(false);
              }}
              onCommit={() => setEndInvalid(parseWallTimeText(fields.endText) === null)}
              onEscape={() => {
                if (!textDirty(PickerTextField.End)) return false;
                resetPickerText(PickerTextField.End);
                setEndInvalid(false);
                return true;
              }}
              onStep={stepTime('end')}
            />
            <span
              className={cx(styles.duration, (timeInvalid || outOfDay) && styles.durationError)}
              data-testid="picker-duration"
              aria-live="polite"
            >
              {timeInvalid
                ? t('picker.invalidTime')
                : outOfDay
                  ? t('picker.outOfDay', { start: formatMin(baseline.dayStartMin) })
                  : diff.span !== null
                    ? durationLabel(diff.span.endMin - diff.span.startMin)
                    : null}
            </span>
          </div>
        </div>

        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="picker-title-input">
            {t('picker.fieldTitle')}
          </label>
          <div className={styles.inputWrap}>
            <input
              id="picker-title-input"
              ref={titleRef}
              className={styles.textInput}
              type="text"
              value={fields.title}
              maxLength={LIMITS.blockTitleMax}
              placeholder={t('picker.titlePlaceholder')}
              data-testid="picker-title"
              onChange={(event) => setPickerText(PickerTextField.Title, event.target.value)}
              onKeyDown={onTitleKeyDown}
              onPaste={onPasteLimited(LIMITS.blockTitleMax, fields.title)}
            />
            {fields.title !== '' ? (
              <button
                type="button"
                className={styles.clear}
                aria-label={t('picker.clearTitle')}
                data-testid="picker-clear-title"
                onClick={() => setPickerText(PickerTextField.Title, '')}
              >
                ×
              </button>
            ) : null}
            {titleCount >= TITLE_COUNTER_FROM ? (
              <span
                className={cx(styles.counter, titleCount >= LIMITS.blockTitleMax && styles.counterMax)}
                data-testid="picker-title-counter"
              >
                {titleCount}/{LIMITS.blockTitleMax}
              </span>
            ) : null}
          </div>
        </div>

        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="picker-note-input">
            {t('picker.fieldNote')}
          </label>
          <div className={styles.inputWrap}>
            <textarea
              id="picker-note-input"
              ref={noteRef}
              className={cx(styles.textInput, styles.noteInput)}
              value={fields.note}
              rows={noteRows}
              maxLength={LIMITS.blockNoteMax}
              placeholder={t('picker.notePlaceholder')}
              data-testid="picker-note"
              onChange={(event) => setPickerText(PickerTextField.Note, event.target.value)}
              onKeyDown={onNoteKeyDown}
              onPaste={onPasteLimited(LIMITS.blockNoteMax, fields.note)}
            />
            {fields.note !== '' ? (
              <button
                type="button"
                className={styles.clear}
                aria-label={t('picker.clearNote')}
                data-testid="picker-clear-note"
                onClick={() => setPickerText(PickerTextField.Note, '')}
              >
                ×
              </button>
            ) : null}
            {noteCount >= NOTE_COUNTER_FROM ? (
              <span
                className={cx(styles.counter, noteCount >= LIMITS.blockNoteMax && styles.counterMax)}
                data-testid="picker-note-counter"
              >
                {noteCount}/{LIMITS.blockNoteMax}
              </span>
            ) : null}
          </div>
        </div>

        {props.creating ? null : (
          <div ref={changeRef}>
            <ProjectDropdown
              projects={props.projects}
              groups={props.groups}
              projectsById={props.projectsById}
              digitProjects={props.digitProjects}
              current={props.currentProject ?? null}
              onChoose={props.onChoose}
            />
          </div>
        )}

        {baseline.isDraft ? <p className={styles.fieldHint}>{t('picker.draftHint')}</p> : null}
        {props.notesDiffer ? (
          <p className={styles.fieldHint} data-testid="picker-note-mismatch">
            {t('picker.noteMismatch', { count: props.mergedCount })}
          </p>
        ) : null}
      </div>

      {props.onOverlay !== undefined ||
      props.onViewConflict !== undefined ||
      props.onDelete !== undefined ? (
        <div className={styles.actions}>
          {props.onOverlay !== undefined ? (
            <button
              type="button"
              className={styles.actionRow}
              data-testid="picker-overlay"
              onClick={props.onOverlay}
            >
              {t('picker.overlay')}
            </button>
          ) : null}
          {props.onViewConflict !== undefined && props.overlapCount > 0 ? (
            <button
              type="button"
              className={styles.actionRow}
              data-testid="picker-view-overlap"
              onClick={props.onViewConflict}
            >
              {t('picker.viewOverlap', { count: props.overlapCount })}
            </button>
          ) : null}
          {props.onDelete !== undefined ? (
            <button
              type="button"
              className={cx(styles.actionRow, styles.dangerRow)}
              data-testid="picker-delete"
              onClick={props.onDelete}
            >
              {deleteLabel}
            </button>
          ) : null}
        </div>
      ) : null}

      <footer className={styles.footer}>
        <Button variant={ButtonVariant.Secondary} onClick={props.onCancel} data-testid="picker-cancel">
          {t('picker.cancel')}
        </Button>
        {/* A draft's primary action saves the fields and confirms the draft in one step. */}
        {props.onConfirm !== undefined && baseline.isDraft ? (
          <Button variant={ButtonVariant.Primary} onClick={props.onConfirm} data-testid="picker-confirm">
            {t('picker.confirm')}
          </Button>
        ) : (
          <Button variant={ButtonVariant.Primary} onClick={saveOrAskProject} data-testid="picker-save">
            {t('picker.save')}
          </Button>
        )}
      </footer>
    </div>
  );
}
